import React, { useCallback, useEffect, useRef, useState } from "react"
import { useConversation } from "@11labs/react"

// ElevenLabs public agent ids per assistant (client-side, as in the source app).
const AGENT_IDS = {
  "tti-receptionist": "mAPEWipD10TSuvN02TO6",
  "bolt-bistro": "q3d4qJ6cpg1IxWkXoJOp",
}

const CALL_MAX_MS = 3 * 60 * 1000 // auto-end after 3 minutes

const AICallWindow = ({ name, onClose }) => {
  const conversation = useConversation({
    onError: error => console.error("ElevenLabs error:", error),
  })
  const [error, setError] = useState("")
  const timerRef = useRef(null)
  const connected = conversation.status === "connected"

  const startConversation = useCallback(async () => {
    setError("")
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      await conversation.startSession({ agentId: AGENT_IDS[name] })
    } catch (err) {
      console.error("Failed to start conversation:", err)
      setError("Could not start the call. Please allow microphone access and try again.")
    }
  }, [conversation, name])

  const stopConversation = useCallback(async () => {
    try {
      await conversation.endSession()
    } catch (err) {
      console.error("Failed to end conversation:", err)
    }
    // Close the modal too — one call per try, no restarting in the same window.
    onClose()
  }, [conversation, onClose])

  // Auto-end after 3 minutes once connected.
  useEffect(() => {
    if (connected) {
      timerRef.current = setTimeout(() => stopConversation(), CALL_MAX_MS)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [connected, stopConversation])

  // End the session if the modal unmounts.
  useEffect(() => () => { conversation.endSession().catch(() => {}) }, []) // eslint-disable-line

  return (
    <div className="aitry-call">
      <span className="aitry-call__note">This call will automatically end in 3 minutes.</span>
      <img src="/ai/agent2.png" alt="AI receptionist" className="aitry-call__avatar" loading="lazy" />
      <div className="aitry-call__actions">
        <button
          type="button"
          onClick={startConversation}
          disabled={connected}
          className="aitry-btn aitry-btn--start"
        >
          Start Call
        </button>
        <button
          type="button"
          onClick={stopConversation}
          disabled={!connected}
          className="aitry-btn aitry-btn--end"
        >
          End Call
        </button>
      </div>
      {error && <p className="aitry-call__error">{error}</p>}
      <p className="aitry-call__status">
        {connected ? "Start talking to our AI powered receptionist." : ""}
      </p>
    </div>
  )
}

export default AICallWindow
